import { EMPTY_OPTION, isEmptyValue } from './emptiness'
import type { ListSchema } from './schema'

export interface FacetIndex {
  readonly sourceMapByFacet: Readonly<
    Record<string, Readonly<Record<string, readonly string[]>>>
  >
  readonly labelsByField: Readonly<
    Record<string, Readonly<Record<string, string>>>
  >
}

export function toFacetOptionIds(value: unknown): string[] {
  if (Array.isArray(value)) {
    if (value.length === 0) return [EMPTY_OPTION]
    return value.map(option => String(option))
  }

  if (isEmptyValue(value)) return [EMPTY_OPTION]
  return [String(value)]
}

export function buildFacetIndex<T>(
  records: readonly T[],
  schema: ListSchema<T>,
): FacetIndex {
  const sourceMapByFacet: Record<
    string,
    Record<string, string[]>
  > = Object.create(null)
  const labelsByField: Record<string, Record<string, string>> = Object.create(
    null,
  )

  for (const field of schema.fields) {
    const shouldCollectLabels =
      !!field.labelValue && !!(field.facetable || field.groupable)
    if (!field.facetable && !shouldCollectLabels) continue

    const sourceMap: Record<string, string[]> | undefined = field.facetable
      ? Object.create(null)
      : undefined
    const labelsForField: Record<string, string> | undefined =
      shouldCollectLabels ? Object.create(null) : undefined
    for (const record of records) {
      const recordId = schema.getId(record)
      const value = field.value(record)
      const optionIds = toFacetOptionIds(value)
      const labelValue = field.labelValue?.(record)
      for (const [index, optionId] of optionIds.entries()) {
        if (sourceMap) {
          sourceMap[optionId] = sourceMap[optionId] ?? []
          sourceMap[optionId].push(recordId)
        }

        const label = labelForValue(value, labelValue, index)
        if (
          labelsForField &&
          optionId !== EMPTY_OPTION &&
          label !== undefined &&
          labelsForField[optionId] === undefined
        ) {
          labelsForField[optionId] = label
        }
      }
    }
    if (sourceMap) sourceMapByFacet[field.key] = sourceMap
    if (labelsForField) labelsByField[field.key] = labelsForField
  }

  return { sourceMapByFacet, labelsByField }
}

export function resolveOptionLabel(
  labelsForField: Readonly<Record<string, string>> | undefined,
  optionId: string,
  emptyOptionLabel: string | undefined,
): string {
  return (
    labelsForField?.[optionId] ??
    (optionId === EMPTY_OPTION ? (emptyOptionLabel ?? optionId) : optionId)
  )
}

function labelForValue(
  value: unknown,
  labelValue: string | readonly string[] | undefined,
  index: number,
): string | undefined {
  if (isEmptyValue(value)) return undefined
  if (Array.isArray(value)) {
    if (!Array.isArray(labelValue)) return undefined
    return labelValue[index]
  }
  return typeof labelValue === 'string' ? labelValue : undefined
}

export function filterByFacets<T>(
  records: readonly T[],
  schema: ListSchema<T>,
  index: FacetIndex,
  appliedFacets: Readonly<Record<string, readonly string[]>>,
): readonly T[] {
  const active = Object.entries(appliedFacets).filter(
    ([, optionIds]) => optionIds.length > 0,
  )
  if (active.length === 0) return records

  const recordIdsByFacet = active.map(([key, optionIds]) => {
    const sourceMap = index.sourceMapByFacet[key] ?? {}
    const ids = new Set<string>()
    for (const optionId of optionIds) {
      for (const recordId of sourceMap[optionId] ?? []) {
        ids.add(recordId)
      }
    }
    return ids
  })

  return records.filter(record => {
    const recordId = schema.getId(record)
    return recordIdsByFacet.every(ids => ids.has(recordId))
  })
}
